import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import MainContainer from "../../../components/container/MainContainer";
import { useNotification } from "../../../components/notification/NotificationContainer";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect } from "react";
import {
  addProposalDetails,
  getProject,
  unsetSelectedProject,
} from "../../../features/projectsSlice";
import Loading from "../../../components/loading/Loading";
import InputField from "../../../components/form/InputField";
import PageTitle from "../../../components/titles/PageTitle";
import EditorField from "../../../components/form/EditorField";
import { addProposalDetailsSchema } from "../../../schemas";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";

const ProposalDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const project = useSelector((state) => state.projects.selected);
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const addProposalLoad = useSelector(
    (state) => state.projects.load.addProposalDetails
  );
  const addNotification = useNotification();
  const {
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      monitoring_supervision: "",
      issues: "",
      program_approach: "",
      sustainability_plan: "",
    },
    resolver: zodResolver(addProposalDetailsSchema),
  });

  const onSubmit = async (data, e) => {
    e.preventDefault();
    try {
      const res = await dispatch(addProposalDetails(data));
      if (addProposalDetails.fulfilled.match(res)) {
        addNotification("Successfully saved!", "success");
        return navigate("/proposals/issues/" + id);
      } else {
        addNotification(res.payload.error, "error");
      }
    } catch (error) {
      addNotification(error.message, "error");
    }
  };

  useLayoutEffect(() => {
    dispatch(getProject(id));

    return () => {
      dispatch(unsetSelectedProject());
    };
  }, []);

  useEffect(() => {
    if (project.id) {
      reset({
        sustainability_plan: project.sustainability_plan || "",
        monitoring_supervision: project.monitoring_supervision || "",
        justification: project.justification || "",
      });
    }
  }, [project]);

  if (
    (id && project.id && project.stage.step_number < 8) ||
    (id && project.id && project.stage.step_number > 8)
  ) {
    return navigate("/requests/my-requests");
  }

  if (projectLoad === loadStatus.loading) {
    return <Loading />;
  }

  if (projectLoad === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-5xl mx-auto">
        <PageTitle title="Planning  - Proposal Details" />
        {project && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            method="post"
            encType="multipart/form-data"
          >
            <div className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto">
              <h5 className="text-text-1/80 ">Proposal Details</h5>
            </div>
            <div className="text-text-1/60  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content">
              <EditorField
                label="Sustainability Plan"
                name="sustainability_plan"
                register={register}
                setValue={setValue}
                errors={errors.sustainability_plan}
                defaultValue={project.sustainability_plan || ""}
              />

              <EditorField
                register={register}
                name="justification"
                label="Justification"
                errors={errors.justification}
                defaultValue={project.justification || ""}
                setValue={setValue}
                type="text"
              />
              <EditorField
                name="monitoring_supervision"
                register={register}
                label="Monitoring & Supervision"
                setValue={setValue}
                defaultValue={project.monitoring_supervision || ""}
                errors={errors.monitoring_supervision}
              />

              <InputField
                register={register}
                name="id"
                type="hidden"
                defaultValue={id}
              />
              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/proposals/background/" + project.id);
                  }}
                  className="cancel-rss-btn"
                  disabled={addProposalLoad}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={addProposalLoad}
                  className="add-rss-btn"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </MainContainer>
  );
};

export default ProposalDetails;
